import { Button, Tooltip } from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import React, { useEffect, useRef, VFC } from 'react';
import { useIntl } from 'react-intl';

import { messages } from 'containers/ChatAgent/messages';
import { ProcessingState } from './index';

import {
  ACCEPTED_KB_FILE_EXTENSIONS,
  TOPIC_EXAMPLE_QUESTIONS,
  TOPIC_PLACEHOLDERS,
} from './constants';
import {
  CHECK_CIRCLE_ICON_SVG,
  DOWNLOAD_ICON_SVG,
  INFO_CIRCLE_ICON_SVG,
  INFO_ICON_SVG,
  UPLOAD_ICON_SVG,
} from './icons';
import { useAgentContextStyles, useTooltipStyles } from './styles';

export interface TopicContent {
  topic: string;
  content: string;
}

export interface KBEditorProps {
  topicContents: TopicContent[];
  onTopicContentChange: (topic: string, content: string) => void;
  onSaveTopic: (topic: string, content: string) => void;
  onUploadContext: (file: File) => void;
  onExport: () => void;
  topicRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  processingState: ProcessingState;
  onDismissSuccessBanner: () => void;
  onViewVersionHistory: () => void;
  showVersionHistory: boolean;
}

export const KBEditor: VFC<KBEditorProps> = ({
  topicContents,
  onTopicContentChange,
  onSaveTopic,
  onUploadContext,
  onExport,
  topicRefs,
  processingState,
  onDismissSuccessBanner,
  onViewVersionHistory,
  showVersionHistory,
}) => {
  const classes = useAgentContextStyles();
  const tooltipClasses = useTooltipStyles();
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const isProcessing = processingState === 'processing';

  useEffect(
    () => () => {
      Object.values(saveTimers.current).forEach(clearTimeout);
    },
    [],
  );

  // Cancel any pending debounced save for a topic that was renamed or
  // deleted — otherwise a stale timer can fire after the topic key it
  // closed over no longer exists, re-creating it via a blind create call.
  useEffect(() => {
    const currentTopics = new Set(topicContents.map(tc => tc.topic));
    Object.keys(saveTimers.current).forEach(topic => {
      if (!currentTopics.has(topic)) {
        clearTimeout(saveTimers.current[topic]);
        delete saveTimers.current[topic];
      }
    });
  }, [topicContents]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadContext(file);
      // eslint-disable-next-line no-param-reassign
      e.target.value = '';
    }
  };

  const handleChange = (topic: string, content: string) => {
    onTopicContentChange(topic, content);
    clearTimeout(saveTimers.current[topic]);
    saveTimers.current[topic] = setTimeout(() => {
      onSaveTopic(topic, content);
    }, 5000);
  };

  return (
    <div className={classes.mainContent}>
      <div className={classes.editorTop}>
        <div className={classes.editorHeader}>
          <span className={classes.editorTitle}>
            {intl.formatMessage(messages.agentContextTitle)}
          </span>
          <div className={classes.editorActions}>
            <Button
              className={classes.viewVersionHistoryButton}
              onClick={onViewVersionHistory}
              startIcon={<HistoryIcon style={{ fontSize: '16px' }} />}
              disableRipple
            >
              {intl.formatMessage(
                showVersionHistory
                  ? messages.hideVersionHistory
                  : messages.viewVersionHistory,
              )}
            </Button>

            <Button
              variant="outlined"
              className={classes.editorActionButton}
              onClick={onExport}
              startIcon={DOWNLOAD_ICON_SVG}
              disabled={isProcessing}
            >
              {intl.formatMessage(messages.exportKnowledgeBase)}
            </Button>

            <Button
              variant="outlined"
              className={classes.editorActionButton}
              onClick={() => fileInputRef.current?.click()}
              startIcon={UPLOAD_ICON_SVG}
              disabled={isProcessing}
            >
              {intl.formatMessage(messages.uploadFiles)}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_KB_FILE_EXTENSIONS}
              hidden
              onChange={handleFileChange}
            />
          </div>
        </div>

        {isProcessing && (
          <div className={classes.processingBanner}>
            <span className={classes.processingBannerIcon}>
              {INFO_CIRCLE_ICON_SVG}
            </span>
            <span className={classes.processingBannerText}>
              {intl.formatMessage(messages.processingBannerTextPaused)}
            </span>
          </div>
        )}

        {processingState === 'completed' && (
          <div className={classes.successBanner}>
            <span className={classes.processingBannerIcon}>
              {CHECK_CIRCLE_ICON_SVG}
            </span>
            <span className={classes.successBannerText}>
              {intl.formatMessage(messages.processingBannerSuccess)}
            </span>
            <Button
              size="small"
              onClick={onDismissSuccessBanner}
              className={classes.successBannerDismiss}
            >
              ✕
            </Button>
          </div>
        )}
      </div>

      <div className={classes.topicsScrollArea}>
        {topicContents.map((tc, index) => {
          const { topic } = tc;
          return (
            <div
              key={topic}
              className={`${classes.topicSection} ${
                isProcessing ? classes.topicSectionDisabled : ''
              }`}
              ref={el => {
                // eslint-disable-next-line no-param-reassign
                topicRefs.current[index] = el;
              }}
            >
              <div className={classes.topicSectionHeader}>
                <div className={classes.topicTitleRow}>
                  <span className={classes.topicSectionTitle}>{topic}</span>
                  {TOPIC_EXAMPLE_QUESTIONS[topic] && (
                    <Tooltip
                      arrow
                      placement="top-start"
                      classes={tooltipClasses}
                      title={
                        <div>
                          <p className={classes.tooltipHeader}>
                            {intl.formatMessage(
                              messages.exampleApplicantQuestions,
                            )}
                          </p>
                          {TOPIC_EXAMPLE_QUESTIONS[topic].map((q, i) => (
                            <p
                              key={q}
                              className={classes.tooltipQuestion}
                              style={{
                                marginBottom:
                                  i < TOPIC_EXAMPLE_QUESTIONS[topic].length - 1
                                    ? '8px'
                                    : 0,
                              }}
                            >
                              {q}
                            </p>
                          ))}
                        </div>
                      }
                    >
                      <span className={classes.topicInfoIcon}>
                        {INFO_ICON_SVG}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
              <textarea
                className={classes.topicTextarea}
                value={tc.content}
                placeholder={
                  TOPIC_PLACEHOLDERS[topic] ??
                  intl.formatMessage(messages.topicContentPlaceholder)
                }
                disabled={isProcessing}
                onChange={e => handleChange(topic, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
